import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useWorkspaceContext } from "@core/context";
import { usePromiseToast, useTranslate } from "@core/hooks";
import { rejectApproval } from "@modules/docs/annotations/services";
import type { ServiceError } from "@core/types/TServiceError";

export function useRejectApproval() {
  const { workspaceId } = useWorkspaceContext();
  const { projectId, docId } = useParams<{ projectId: string; docId: string }>();
  const [searchParams] = useSearchParams();
  const approvalId = searchParams.get("approval_id") ?? "";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { successKey, errorKey } = usePromiseToast();
  const { t } = useTranslate("documents");

  return useMutation({
    mutationFn: (rejection_reasons: string[]) =>
      rejectApproval(workspaceId, projectId!, approvalId, rejection_reasons),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["approval-info", workspaceId, projectId, approvalId],
      });
      queryClient.invalidateQueries({
        queryKey: ["approval-annotations", workspaceId, projectId, approvalId],
      });
      queryClient.invalidateQueries({
        queryKey: ["approvals", "submitted_to_me"],
      });
      queryClient.invalidateQueries({
        queryKey: ["approvals", "my_submissions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["approvals", "all"],
      });
      queryClient.invalidateQueries({ queryKey: ["doc-file", docId] });
      successKey(t, "annotations.reject-success");
      setTimeout(
        () =>
          navigate(
            `/workspace/${workspaceId}/project-portal/${projectId}/approvals`,
          ),
        500,
      );
    },
    onError: (err: ServiceError) => {
      errorKey(t, "errors." + err.code, err.fields);
    },
  });
}
